import { Injectable } from '@angular/core';
import { BehaviorSubject, map, Observable } from 'rxjs';
import { CommonResponse, OtpData, OtpPayload } from '../models/common.model';
import { HttpClient } from '@angular/common/http';
import { environment } from '../../environments/environment';
import {
  AvailabilityData,
  ChangePasswordPayload,
  LoginPayload,
  ResetOtpPayload,
  ResetPasswordPayload,
  ResetPasswordResponse,
  StudentOnboardingData,
  TutorExpertiseResult,
  UserResponse,
  UserSignUpPayload
} from '../models/auth.model';
import Swal from 'sweetalert2';
import { TranslateService } from '@ngx-translate/core';
import { NotificationResponse } from '../models/notification.model';

@Injectable({
  providedIn: 'root'
})
export class AuthService {
  emailSubject: BehaviorSubject<{ email: string } | null> =
    new BehaviorSubject<{ email: string } | null>(null);
  chatClicked: BehaviorSubject<boolean> =
    new BehaviorSubject<boolean>(false);
  permissionUpdate: BehaviorSubject<any> =
    new BehaviorSubject<any>(null);
  supportClicked: BehaviorSubject<boolean> =
    new BehaviorSubject<boolean>(false);
  notificationClicked: BehaviorSubject<boolean> =
    new BehaviorSubject<boolean>(false);
  resetTokenSubject: BehaviorSubject<string> = new BehaviorSubject<string>('');
  public profileDetailsSubject = new BehaviorSubject<UserResponse | null>(null);
  profileDetails$ = this.profileDetailsSubject.asObservable();
  private isOpenSubject = new BehaviorSubject<boolean>(false);
  isOpen$ = this.isOpenSubject.asObservable();
  deviceTokenSubject: BehaviorSubject<any> = new BehaviorSubject<any>(
    'test'
  );
  constructor(private http: HttpClient, private translateService: TranslateService) {}

  toggleSidebarView(): void {
    this.isOpenSubject.next(!this.isOpenSubject.value);
  }

  isAuthenticated(): boolean {
    if (
      localStorage.getItem(environment.storageKey) &&
      localStorage.getItem(environment.storageKey) !== null
    ) {
      return true;
    } else {
      return false;
    }
  }

  register(payload: UserSignUpPayload, query = ''): Observable<CommonResponse> {
    return this.http.post<CommonResponse>(
      `${environment.base_url}user/register?${query}`,
      payload
    );
  }

  /**
   * <T> will take up the dynamic interface from the component it is being called
   */
  verifyOtp<T>(payload: OtpPayload, query = ''): Observable<T> {
    return this.http.post<T>(
      `${environment.base_url}user/verifyOtp?${query}`,
      payload
    );
  }

  resendOtp(payload: ResetOtpPayload): Observable<CommonResponse> {
    return this.http.post<CommonResponse>(
      `${environment.base_url}user/resendOtp`,
      payload
    );
  }

  login<T>(payload: LoginPayload): Observable<T> {
    return this.http.post<T>(`${environment.base_url}user/login`, payload);
  }

  socialLogin(payload: any): Observable<any> {
    return this.http.post<any>(`${environment.base_url}user/socialLogin`, payload);
  }

  getDecodedJson(payload: {token: string}): Observable<any> {
    return this.http.post<any>(`${environment.base_url}user/getDecodedToken`, payload).pipe(map(res => res.data));
  }

  forgotPassword(email: string): Observable<CommonResponse> {
    return this.http.post<CommonResponse>(
      `${environment.base_url}user/forgotPassword`,
      email
    );
  }

  resetPassword(data: ResetPasswordPayload): Observable<ResetPasswordResponse> {
    return this.http
      .post<ResetPasswordResponse>(
        `${environment.base_url}user/resetPassword`,
        data
      )
      .pipe(map(res => res));
  }

  changePassword(payload: ChangePasswordPayload): Observable<CommonResponse> {
    return this.http.post<CommonResponse>(`${environment.base_url}user/changePassword`, payload);
  }

  getAvaliablityListing(): Observable< AvailabilityData[]> {
    return this.http
      .get<{ data: AvailabilityData[] }>(`${environment.base_url}user/getTutorAvailability`)
      .pipe(map(res => res.data));
  }

  addAvaliablity(payload: unknown): Observable< AvailabilityData[]> {
    return this.http
      .post<{ data:  AvailabilityData[] }>(`${environment.base_url}user/addTutorAvailability`, payload)
      .pipe(map(res => res.data));
  }

  deleteAvaliablity(payload: any): Observable<any> {
    return this.http.post<{ data: any }>(`${environment.base_url}user/deleteTutorAvailability`, payload)
      .pipe(map(res => res));
  }

  getProfile(): Observable<UserResponse> {
    return this.http
      .get<{ data: UserResponse }>(`${environment.base_url}user/get`)
      .pipe(map(res => res.data));
  }

  updateProfile(payload: unknown): Observable<UserResponse> {
    return this.http
      .put<{ data: UserResponse }>(`${environment.base_url}user/edit`, payload)
      .pipe(map(res => res.data));
  }

  getActivityLogs(query: string): Observable<any> {
    return this.http
      .get<{ data: any }>(`${environment.base_url}activityLogs?${query}`)
      .pipe(map(res => res.data));
  }

  logout(): Observable<CommonResponse> {
    return this.http.post<CommonResponse>(
      `${environment.base_url}user/logout`,
      {}
    );
  }

  validateAccount(payload: OtpData): Observable<CommonResponse> {
    return this.http.post<CommonResponse>(
      `${environment.base_url}user/validateAccount`,
      payload
    );
  }

  verifyValidateAccount(
    payload: OtpPayload,
    query = ''
  ): Observable<CommonResponse> {
    return this.http.post<CommonResponse>(
      `${environment.base_url}user/verifyValidate?${query}`,
      payload
    );
  }

  getNotificationHistory(query: string): Observable<NotificationResponse> {
    return this.http
      .get<{
        data: NotificationResponse;
      }>(`${environment.base_url}notifications/myNotifications?${query}`)
      .pipe(map(res => res.data));
  }

  getNotificationCount(): Observable<any> {
    return this.http
      .get<{
        data: any;
      }>(`${environment.base_url}/user/getNotificationCount`)
      .pipe(map(res => res.data));
  }


  getChatCount(): Observable<any> {
    return this.http
      .get<{
        data: any;
      }>(`${environment.base_url}/user/getChatCount`)
      .pipe(map(res => res.data));
  }

  getSupportCount(): Observable<any> {
    return this.http
      .get<{
        data: any;
      }>(`${environment.base_url}/user/getSupportTicketCount`)
      .pipe(map(res => res.data));
  }

  getExpertiseData(): Observable<TutorExpertiseResult> {
    return this.http
      .get<{
        data: TutorExpertiseResult;
      }>(`${environment.base_url}user/getExpertiseOnboarding`)
      .pipe(map(res => res.data));
  }

  deleteAccount(): Observable<any> {
    return this.http.delete<{ data: any }>(`${environment.base_url}user/deleteAccount`)
      .pipe(map(res => res));
  } 
  
  unlinkStudent(id: string): Observable<any> {
    return this.http.delete<{ data: any }>(`${environment.base_url}user/unLinkStudent/${id}`)
      .pipe(map(res => res));
  } 

  getStudentOnboardingData(): Observable<StudentOnboardingData> {
    return this.http
      .get<{
        data: StudentOnboardingData;
      }>(`${environment.base_url}categories/subOnboarding`)
      .pipe(map(res => res.data));
  }

  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  passwordChangedLogout(): Promise<any> {
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    return new Promise((resolve: any) => {
      Swal.fire({
        title: this.translateService.instant('PasswordUpdatedSuccessfully'),
        text: this.translateService.instant('Youwillbeloggedout'),
        icon: 'success'
        // eslint-disable-next-line @typescript-eslint/no-explicit-any
      }).then((result: any) => {
        return resolve(result);
      });
    });
  }

  invoicePayOut(payload: any): Observable<any> {
    return this.http.post<any>(
      `${environment.base_url}user/payOutsideApp`,
      payload
    );
  }

  get currentProfileDetails(): UserResponse | null {
    return this.profileDetailsSubject.value;
  }
}
